// Floating bulk-action bar for selected rows (adapted from the List view's bar).
import { Trash2, X } from '@/components/ui/icons';
import type { Task } from '@shared/types';
import { getDb, useDb, useDbShallow } from '@/lib/store';
import { effectiveStatuses } from '@/lib/hierarchy';
import { deleteTask, duplicateTask, updateTask } from '@/lib/actions';
import { Popover } from '@/components/ui/Popover';
import { toast } from '@/components/ui/overlays';
import { StatusMenu } from '@/components/fields/Status';
import { PeopleMenu } from '@/components/fields/People';
import { PriorityMenu } from '@/components/fields/Priority';
import { DateRangeMenu } from '@/components/fields/DatePicker';
import { MoveTaskMenu } from '../MoveTaskMenu';

export function TableBulkBar({ ids, clear }: { ids: string[]; clear: () => void }) {
  const first = useDb((d) => (ids[0] ? d.tasks[ids[0]] : undefined));
  const statuses = useDbShallow((d) => (first ? effectiveStatuses(d, first.listId) : []));
  if (!first || ids.length === 0) return null;
  const apply = (changes: Partial<Task>) => ids.forEach((id) => updateTask(id, changes));
  return (
    <div className="bulk-bar tv-bulk">
      <span className="bulk-count">
        <input type="checkbox" className="checkbox" checked readOnly onClick={clear} />
        {ids.length} Task{ids.length > 1 ? 's' : ''} selected
      </span>
      <span className="bulk-sep" />
      <Popover
        placement="top"
        content={(close) => (
          <StatusMenu
            statuses={statuses}
            onPick={(s) => {
              for (const id of ids) {
                const t = getDb().tasks[id];
                if (!t) continue;
                const target = effectiveStatuses(getDb(), t.listId).find((x) => x.name.toLowerCase() === s.name.toLowerCase());
                if (target) updateTask(id, { statusId: target.id });
              }
              close();
            }}
          />
        )}
      >
        <button className="bulk-btn">Status</button>
      </Popover>
      <Popover
        placement="top"
        content={() => (
          <PeopleMenu
            selected={[]}
            onChange={(u) =>
              ids.forEach((id) => {
                const t = getDb().tasks[id];
                if (t) updateTask(id, { assigneeIds: [...new Set([...t.assigneeIds, ...u])] });
              })
            }
          />
        )}
      >
        <button className="bulk-btn">Assignees</button>
      </Popover>
      <Popover placement="top" content={(close) => <DateRangeMenu start={null} due={null} onChange={(_, due) => apply({ dueDate: due })} onClose={close} />}>
        <button className="bulk-btn">Dates</button>
      </Popover>
      <Popover placement="top" content={(close) => <PriorityMenu value={null} onPick={(p) => (apply({ priority: p }), close())} />}>
        <button className="bulk-btn">Priority</button>
      </Popover>
      <Popover placement="top" content={(close) => <MoveTaskMenu taskIds={ids} onDone={() => (close(), clear())} />}>
        <button className="bulk-btn">Move</button>
      </Popover>
      <button className="bulk-btn" onClick={() => (ids.forEach((id) => duplicateTask(id)), toast(`${ids.length} task(s) duplicated`))}>
        Duplicate
      </button>
      <button
        className="bulk-btn danger"
        title="Delete"
        onClick={() => {
          if (confirm(`Delete ${ids.length} task(s)?`)) {
            ids.forEach((id) => deleteTask(id));
            clear();
          }
        }}
      >
        <Trash2 size={14} />
      </button>
      <button className="icon-btn sm" onClick={clear} title="Clear selection">
        <X size={14} />
      </button>
    </div>
  );
}
